// File: js/shared/utils/blockFactory.js

import { i18n } from './config.js';

export function createDynamicBlock(blockName, type, index) {
  const prefix = `${blockName}[${index}]`;

  const labels = {
    text:    i18n('inputs.block.text_label'),
    image:   i18n('block.upload_label.image'),
    audio:   i18n('block.upload_label.audio'),
    video:   i18n('block.upload_label.video'),
    podcast: i18n('block.upload_label.podcast'),

    // Module items (child references)
    mquiz:    i18n('block.upload_label.module_quiz'),
    mpodcast: i18n('block.upload_label.module_podcast'),
    mimage:   i18n('block.upload_label.module_image'),
    mvideo:   i18n('block.upload_label.module_video'),
    mrumeur:  i18n('block.upload_label.module_rumeur'),
    mcouloir: i18n('block.upload_label.module_couloir'),
  };

  const buildModuleInner = (subtype) => {
    const selName     = `${prefix}[child_content_id]`;
    const minutesName = `${prefix}[estimated_minutes]`;
    const hiddenType  = `${prefix}[type]`;
    const fieldLabel  = `${i18n('block.upload_label.module_' + subtype)} :`;
    const minutesLabel = `${i18n('inputs.block.default_minutes_label')} :`;

    return `
      <div class="form-row space-after">
        <label for="${selName}">${fieldLabel}</label><br/>
        <select name="${selName}" class="module-child-select" data-subtype="${subtype}" required>
          <option value="" disabled selected>${i18n('block.module_ref.loading_option')}</option>
        </select>
        <div class="hint">${i18n('content.module.dynamic_blocks.select_hint')}</div>
      </div>

      <div class="form-row space-after">
        <label for="${minutesName}">${minutesLabel}</label><br/>
        <input type="number" min="1" step="1" name="${minutesName}" value="" required>
        <div class="hint">${i18n('content.module.dynamic_blocks.minutes_hint')}</div>
      </div>

      <input type="hidden" name="${hiddenType}" value="${subtype}">
      <input type="hidden" name="${prefix}[as_child]" value="1">
    `;
  };

  let toolbarHtml = '';
  let inputHtml = '';
  const label = labels[type] || i18n('block.upload_label.fallback');

  if (type === 'text') {
    inputHtml = `
      <div class="richtext" contenteditable="true" data-name="${prefix}[content]"></div>
      <input type="hidden" name="${prefix}[content]" value="">
      <input type="hidden" name="${prefix}[type]" value="text">`;

    toolbarHtml = `
      <div class="block-toolbar">
        <span class="rt-tb-group">
          <button type="button" data-action="bold" title="${i18n('block.toolbar.bold_title')}"><b>B</b></button>
          <button type="button" data-action="italic" title="${i18n('block.toolbar.italic_title')}"><i>I</i></button>
          <button type="button" data-action="underline" title="${i18n('block.toolbar.underline_title')}"><u>U</u></button>
        </span>
        <span class="rt-menu" data-list-menu>
          <button type="button" class="rt-menu__trigger" data-list-trigger title="${i18n('block.toolbar.lists_title')}"><span class="rt-ic-list" aria-hidden="true"></span><span class="rt-caret" aria-hidden="true"></span></button>
          <div class="rt-menu__pop" hidden>
            <button type="button" class="rt-menu__item" data-list-cmd="insertUnorderedList"><span class="rt-ic-ul" aria-hidden="true"></span>${i18n('block.toolbar.bullet_list_title')}</button>
            <button type="button" class="rt-menu__item" data-list-cmd="insertOrderedList"><span class="rt-ic-ol" aria-hidden="true"></span>${i18n('block.toolbar.numbered_list_title')}</button>
          </div>
        </span>
        <span class="rt-menu" data-link-menu>
          <button type="button" class="rt-menu__trigger" data-link-trigger title="${i18n('block.toolbar.link_title')}"><span class="rt-ic-link" aria-hidden="true"></span></button>
          <div class="rt-linkpop__pop" hidden>
            <input type="text" class="rt-linkpop__input" data-link-input placeholder="https://…" autocomplete="off" spellcheck="false" aria-label="${i18n('block.toolbar.link_url_label')}">
            <span class="rt-linkpop__actions">
              <button type="button" class="rt-linkpop__btn" data-link-apply>${i18n('block.toolbar.link_apply')}</button>
              <button type="button" class="rt-linkpop__btn rt-linkpop__btn--remove" data-link-remove hidden>${i18n('block.toolbar.link_remove')}</button>
            </span>
          </div>
        </span>
        <span class="rt-sizebox" title="${i18n('block.toolbar.font_size_title')}">
          <span class="rt-ic-size" aria-hidden="true">A</span>
          <input type="text" class="rt-sizebox__input" data-size-input inputmode="numeric" maxlength="2" autocomplete="off" aria-label="${i18n('block.toolbar.font_size_title')}">
          <span class="rt-sizebox__steps">
            <button type="button" class="rt-sizebox__step" data-size-step="1" tabindex="-1" aria-label="+">▴</button>
            <button type="button" class="rt-sizebox__step" data-size-step="-1" tabindex="-1" aria-label="−">▾</button>
          </span>
        </span>
      </div>`;
  }

  else if (type === 'podcast') {
    inputHtml = `
      <label for="${prefix}[podcast_id]">${i18n('block.podcast.episode_field_label')} :</label>
      <select name="${prefix}[podcast_id]" class="podcast-episode-select" required data-dynamic-podcast-options>
        <option value="" disabled selected>${i18n('block.podcast.loading_option')}</option>
      </select>
      <div class="podcast-player-preview" style="margin-top: 1rem;"></div>
      <div class="podcast-description" style="margin-top: 0.5rem;"></div>
      <input type="hidden" name="${prefix}[type]" value="podcast">`;
  }

  else if (['mquiz','mpodcast','mimage','mvideo','mrumeur','mcouloir'].includes(type)) {
    const subtype = type.slice(1); // strip 'm'
    inputHtml = buildModuleInner(subtype);
  }

  else if (type.endsWith('_ref')) {
    // Child-reference token from the section's JSON (`podcast_ref`,
    // `quiz_ref`, …): the module create form's add buttons emit these, and
    // this factory used to fall through to the media branch and render a
    // file input for a chapter (found 2026-09-13). The posted `[type]` is
    // the storage type, as `ChildReferenceInserter` + the ENUM expect.
    inputHtml = buildModuleInner(type.slice(0, -4));
  }

  else {
    // Base media blocks: image / audio / video
    const accept = (type === 'image') ? 'image/*' : (type === 'audio') ? 'audio/*' : (type === 'video') ? 'video/*' : '*/*';
    const imageSizeLabel = i18n('block.image.size_label_format', { percent: 100 });
    inputHtml = `
      <input type="file" name="${prefix}[media]" accept="${accept}" required>
      <input type="hidden" name="${prefix}[dimensions]" value="">
      <div class="file-preview"></div>
      ${
        type === 'image'
          ? `<label class="image-size-label" style="display:none">${imageSizeLabel}</label>
             <input type="range" class="image-size-slider" name="${prefix}[display_size]" min="1" max="100" value="100" style="display:none">`
          : ''
      }`;
  }

  return `
    <div class="block" data-type="${type}">
      <div class="block-header">
        <span class="block-label">${label}</span>
        <div class="block-actions">
          ${toolbarHtml}
          <button type="button" data-action="up" title="${i18n('block.action.move_up')}">↑</button>
          <button type="button" data-action="down" title="${i18n('block.action.move_down')}">↓</button>
          <button type="button" data-action="delete" title="${i18n('block.action.delete')}">✕</button>
        </div>
      </div>
      ${inputHtml}
      ${
        (['mquiz','mpodcast','mimage','mvideo','mrumeur','mcouloir'].includes(type) || type.endsWith('_ref') || type === 'text' || type === 'podcast')
          ? ''
          : `<input type="hidden" name="${prefix}[type]" value="${type}">`
      }
    </div>`;
}
